"use client";

import { useMarketingAnalytics } from "@analytics";
import { useEffect, useRef } from "react";

const SKIP_ATTR = "data-analytics-skip-outbound";

/**
 * Delegated capture listener for `mailto:` and cross-origin links site-wide.
 * Consent-gated via {@link useMarketingAnalytics}.
 */
export function OutboundLinkAnalyticsListener() {
	const { siteMailtoLinkClicked, siteExternalLinkClicked } =
		useMarketingAnalytics();
	const handlersRef = useRef({
		siteMailtoLinkClicked,
		siteExternalLinkClicked,
	});
	handlersRef.current = { siteMailtoLinkClicked, siteExternalLinkClicked };

	useEffect(() => {
		const onClickCapture = (event: MouseEvent) => {
			if (!(event.target instanceof Element)) {
				return;
			}
			const anchor = event.target.closest("a[href]");
			if (!(anchor instanceof HTMLAnchorElement)) {
				return;
			}
			if (anchor.closest(`[${SKIP_ATTR}]`)) {
				return;
			}
			const hrefAttr = anchor.getAttribute("href");
			if (!hrefAttr || hrefAttr.startsWith("#")) {
				return;
			}

			const page_path = window.location.pathname;
			const href = anchor.href;

			if (/^mailto:/i.test(href)) {
				handlersRef.current.siteMailtoLinkClicked({ href, page_path });
				return;
			}

			try {
				const resolved = new URL(href, window.location.origin);
				if (resolved.origin !== window.location.origin) {
					handlersRef.current.siteExternalLinkClicked({
						href: resolved.href,
						host: resolved.host,
						page_path,
					});
				}
			} catch {
				/* ignore invalid href */
			}
		};

		document.addEventListener("click", onClickCapture, true);
		return () => {
			document.removeEventListener("click", onClickCapture, true);
		};
	}, []);

	return null;
}
